/**
 * usePilotChat — WebSocket hook for the Pilot orchestrator agent.
 *
 * Connects to /api/pilot/ws (text-only, no STT/TTS) and provides
 * message send/receive, step status tracking, and workspace action events.
 */
import { useState, useCallback, useRef, useEffect } from 'react';

export interface PilotMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface StepStatusUpdate {
  stepId: string;
  status: string;
}

export interface StepOpenEvent {
  stepId: string;
  solutionId?: string | null;
}

interface UsePilotChatOptions {
  subProjectId: string;
  subProjectName: string;
  kbId?: string;
  kbName?: string;
  solutionId?: string | null;
  language: string;
  onStepOpen?: (event: StepOpenEvent) => void;
}

export function usePilotChat(options: UsePilotChatOptions) {
  const {
    subProjectId,
    subProjectName,
    kbId,
    kbName,
    solutionId,
    language,
    onStepOpen,
  } = options;

  const [messages, setMessages] = useState<PilotMessage[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const isMountedRef = useRef(true);
  const pingIntervalRef = useRef<number | null>(null);
  const onStepOpenRef = useRef(onStepOpen);

  useEffect(() => { onStepOpenRef.current = onStepOpen; }, [onStepOpen]);

  // Build WS URL
  const getWsUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let host = window.location.host;

    const apiBase = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL;
    if (apiBase) {
      try {
        const url = new URL(apiBase);
        host = url.host;
      } catch { /* fallback */ }
    } else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      if (window.location.port !== '8001') {
        host = `${window.location.hostname}:8001`;
      }
    }

    return `${protocol}//${host}/api/pilot/ws`;
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const token = sessionStorage.getItem('access_token');
    if (!token) return;

    setIsConnecting(true);
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      // If component unmounted while we were connecting, abort cleanly without warning
      if (!isMountedRef.current) {
        ws.close(1000, 'Component unmount');
        return;
      }
      ws.send(JSON.stringify({
        type: 'auth',
        token,
        sub_project_id: subProjectId,
        sub_project_name: subProjectName,
        kb_id: kbId || null,
        kb_name: kbName || '',
        solution_id: solutionId || null,
        language,
      }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
          case 'ready':
            if (isMountedRef.current) {
              setIsReady(true);
              setIsConnecting(false);
            }
            if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = window.setInterval(() => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'ping' }));
              }
            }, 30000);
            break;

          case 'auth_error':
            if (isMountedRef.current) {
              setIsConnecting(false);
              setIsReady(false);
            }
            ws.close(4401);
            break;

          case 'sentence':
            if (isMountedRef.current) {
              setIsProcessing(false);
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  // Append to existing assistant message
                  const updated = [...prev];
                  updated[updated.length - 1] = { ...last, content: last.content + '\n' + msg.content };
                  return updated;
                }
                return [...prev, { role: 'assistant', content: msg.content }];
              });
            }
            break;

          case 'response_complete':
            if (isMountedRef.current) {
              setIsProcessing(false);
            }
            break;

          case 'workspace_action':
            if (msg.action === 'step_workspace_open_requested') {
              onStepOpenRef.current?.({
                stepId: msg.step_id,
                solutionId: msg.solution_id,
              });
            }
            break;

          case 'error':
            if (isMountedRef.current) {
              setIsProcessing(false);
              setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${msg.content}` }]);
            }
            break;

          case 'pong':
            break;
        }
      } catch (e) {
        console.error('[PilotChat] Parse error:', e);
      }
    };

    ws.onclose = (event) => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (isMountedRef.current) {
        setIsReady(false);
        setIsConnecting(false);
      }
      wsRef.current = null;
      // Auto-reconnect on unexpected close
      if (event.code !== 1000 && event.code !== 4401 && isMountedRef.current) {
        setTimeout(() => {
          if (isMountedRef.current && !wsRef.current) connect();
        }, 2000);
      }
    };

    ws.onerror = () => {
      if (isMountedRef.current) {
        setIsConnecting(false);
        setIsReady(false);
      }
    };
  }, [getWsUrl, subProjectId, subProjectName, kbId, kbName, solutionId, language]);

  // Send text message
  const sendMessage = useCallback((text: string) => {
    if (!text.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setIsProcessing(true);
    wsRef.current.send(JSON.stringify({ type: 'message', content: text }));
  }, []);

  // Update solution context
  const updateSolution = useCallback((newSolutionId: string | null) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'update_solution', solution_id: newSolutionId }));
    }
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    isMountedRef.current = true;
    if (subProjectId) connect();
    return () => {
      isMountedRef.current = false;
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        const state = wsRef.current.readyState;
        if (state === WebSocket.OPEN) {
          // Only close if fully open — closing a CONNECTING socket triggers a browser warning
          try { wsRef.current.close(1000, 'Component unmount'); } catch { /* */ }
        }
        // If CONNECTING: onopen will check isMountedRef and close after handshake completes
        wsRef.current = null;
      }
    };
  }, [connect, subProjectId]);

  return {
    messages,
    isReady,
    isConnecting,
    isProcessing,
    sendMessage,
    updateSolution,
  };
}
